// src/utils/firebase/ebayToSales.js
// Promotes staged eBay items into sale documents once they are live on eBay.

import { collection, doc, writeBatch, Timestamp, getDoc } from 'firebase/firestore';
import { db } from './firebase.utils';
import { sanitizeForFirestore } from './serialization';
import { SALE_KIND, SALE_STATUSES, isStaged } from '../sales/sales.constants';
import { attachSaleIdToGroupCode } from './groupCodes';
import { calculateNetProfit } from '../sales/salesMath';
import { isLotItem, buildTitle } from '../whatnot/whatnotListingText';
import { buildEbayTitle } from '../ebay/ebayListingText';
import { transactionDoc, transactionsCollection } from './transactionPaths';
import { updateShowCard } from './whatnotShow';
import { mintEbaySkus } from './ebay';

// Firestore caps a batch at 500 writes; leave headroom for a single large lot.
const MAX_BATCH_OPS = 400;

/**
 * Builds the structured collection pointer used to locate the card later.
 * Returns null for manually added items, which have no checklist context.
 */
const buildCardPath = (src) => {
  const { sport, year, manufacturer, series, cardType, cardTypeCategory } = src ?? {};
  if (!sport || !year || !manufacturer || !series || !cardType || !cardTypeCategory) return null;
  return { sport, year, manufacturer, series, cardType, cardTypeCategory };
};

const buildChecklistPath = (cardPath) => {
  if (!cardPath) return null;
  const { sport, year, manufacturer, series, cardType, cardTypeCategory } = cardPath;
  return `/checklists/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}`;
};

/**
 * Converts a staged eBay card (a single item, or one entry of a lot's embedded
 * cards) into a sale card snapshot. Both carry the same flat field shape.
 */
const buildCardSnapshot = (src) => {
  const cardPath = buildCardPath(src);

  return {
    cardNumber: src.cardNumber ?? null,
    name: src.name ?? [],
    team: src.team ?? [],
    cardAttributes: src.cardAttributes ?? [],
    checklistPath: buildChecklistPath(cardPath),
    cardPath,
    // Manually added cards have no cardPath but can still name their set, which
    // is all the sale's card list has to group them under.
    series: src.series ?? null,
    variation: {
      cardParallelName: src.cardParallelName ?? null,
      cardColor: src.cardColor ?? null,
      cardQty: 1,
      cardPrintNumber: src.cardPrintNumber ?? null,
      cardSupply: src.cardSupply ?? null
    },
    isCollected: true,
    quantity: 1,
    releasedFromCollection: false
  };
};

const listingTitle = (item) => {
  if (isLotItem(item)) return item.title?.trim() || 'Untitled Lot';
  if ((item.platform || 'eBay') === 'eBay') {
    return buildEbayTitle(item, item.series, item) || 'Untitled Listing';
  }
  return buildTitle(item) || 'Untitled Listing';
};

const buildSaleDoc = (item, cardCount) => {
  const platform = item.platform || 'eBay';
  const fields = {
    title: listingTitle(item),
    kind: SALE_KIND,
    status: SALE_STATUSES.LISTED,
    platform,
    source: platform.toLowerCase(),
    // Carried from the queue document, which is deleted on promotion. This is
    // what an imported eBay or Whatnot order's SKU is matched against.
    ebaySku: item.sku ?? null,
    sku: item.sku ?? null,
    listingFormat: item.format ?? (platform === 'eBay' ? 'FixedPrice' : null),
    price: Number(item.price) || 0,
    cardCount,
    salesTax: 0,
    buyerShipping: 0,
    shippingCost: 0,
    transactionFee: 0,
    platformFee: 0,
    packingCost: 0,
    notes: ''
  };

  return { ...fields, netProfit: calculateNetProfit(fields) };
};

/**
 * Promotes staged eBay items into sale documents and clears them from the queue.
 *
 * Each item becomes one sale document with one card snapshot per card, and the
 * source eBay document is deleted, so a listing lives in exactly one place.
 *
 * @param {string} uid - User ID
 * @param {Array<Object>} items - Staged eBay items (singles and/or lots)
 * @returns {Promise<{saleIds: string[], cardCount: number}>}
 */
export async function promoteEbayItemsToSales(uid, items) {
  if (!uid || !Array.isArray(items) || items.length === 0) {
    return { saleIds: [], cardCount: 0 };
  }

  const salesColRef = transactionsCollection(uid);
  const saleIds = [];
  const codeAttachments = [];
  let totalCards = 0;

  const prepared = [];
  for (const rawItem of items) {
    if (rawItem.sku) {
      prepared.push(rawItem);
      continue;
    }
    const source = String(rawItem.platform || 'ebay').toLowerCase().replace(/[^a-z0-9]/g, '') || 'sale';
    const [sku] = await mintEbaySkus(uid, 1, source);
    prepared.push({ ...rawItem, sku });
  }

  let batch = writeBatch(db);
  let batchOps = 0;
  const pendingCommits = [];

  for (const item of prepared) {
    const sourceCards = isLotItem(item) ? (item.cards ?? []) : [item];
    const cards = sourceCards.map(buildCardSnapshot);
    const itemOps = cards.length + 2; // sale doc + card docs + queue deletion

    if (batchOps > 0 && batchOps + itemOps > MAX_BATCH_OPS) {
      pendingCommits.push(batch.commit());
      batch = writeBatch(db);
      batchOps = 0;
    }

    const saleRef = doc(salesColRef);
    const now = Timestamp.now();

    batch.set(saleRef, {
      ...sanitizeForFirestore(buildSaleDoc(item, cards.length)),
      listingDate: now,
      saleDate: null,
      createdAt: now,
      updatedAt: now
    });

    for (const card of cards) {
      batch.set(doc(collection(saleRef, 'cards')), {
        ...sanitizeForFirestore(card),
        createdAt: now
      });
    }

    batch.delete(transactionDoc(uid, item.id));

    batchOps += itemOps;
    saleIds.push(saleRef.id);
    if (item.sku) codeAttachments.push({ sku: item.sku, saleId: saleRef.id });
    totalCards += cards.length;
  }

  if (batchOps > 0) pendingCommits.push(batch.commit());
  await Promise.all(pendingCommits);

  await Promise.all(
    codeAttachments.map(({ sku, saleId }) => attachSaleIdToGroupCode(uid, sku, saleId))
  );

  return { saleIds, cardCount: totalCards };
}

/**
 * Promotes the staged transaction a show card was copied from, then records
 * the new sale on the show card. The show's sold toggle is left alone.
 *
 * @param {string} uid
 * @param {string} showId
 * @param {Object} showCard
 * @returns {Promise<string|null>} Listed sale id
 */
export async function markShowCardListed(uid, showId, showCard) {
  if (!uid || !showId || !showCard?.sourceTransactionId) return null;
  if (showCard.listedSaleId) return showCard.listedSaleId;

  const sourceRef = transactionDoc(uid, showCard.sourceTransactionId);
  const sourceSnap = await getDoc(sourceRef);
  if (!sourceSnap.exists()) return null;

  const item = { id: sourceSnap.id, ...sourceSnap.data() };
  if (!isStaged(item.status)) return null;

  const price = showCard.finalBid != null && showCard.finalBid !== ''
    ? showCard.finalBid
    : item.price;

  const { saleIds } = await promoteEbayItemsToSales(uid, [{ ...item, price }]);
  const saleId = saleIds[0] ?? null;
  if (!saleId) return null;

  await updateShowCard(uid, showId, showCard.id, { listedSaleId: saleId });
  return saleId;
}
